import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  getWipReportsByProjectIds,
  getKeyDatesByProjectSFId,
} from '../../../entities/project/service'
import { getWipAiStaffUtilization } from '../../../entities/wipReportAi/service'
import { removeTypename } from '../../../utils/removeTypename'
import {
  buildMonthsQueryString,
  buildWeeklyPacing,
  monthPacingUsd,
  deriveTrend,
  computeForecastEom,
  buildHistorySparks,
  reportingPeriodLabel,
} from './pacing'

/**
 * Loads WIP report row + key dates + staff utilization for the WipReportAi canvas (non-AI sections).
 * @param {string | null} selectedMonth - YYYY-MM from resolver rows; when null, derived metrics are empty until set.
 */
export function useWipReportAiData({ client, selectedProject, selectedMonth }) {
  const today = useMemo(() => moment().format('YYYY-MM-DD'), [])
  const monthsParam = useMemo(() => buildMonthsQueryString(), [])

  const [wipRow, setWipRow] = useState(null)
  const [keyDates, setKeyDates] = useState([])
  const [staffUtilization, setStaffUtilization] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!client || !selectedProject?._id) {
      setWipRow(null)
      setKeyDates([])
      setStaffUtilization([])
      setError(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    setWipRow(null)
    setStaffUtilization([])
    ;(async () => {
      try {
        const projectId = String(selectedProject._id)
        const utilPromise =
          selectedMonth != null && selectedMonth !== ``
            ? getWipAiStaffUtilization(client, projectId, selectedMonth)
            : Promise.resolve([])

        const [wipReports, kdResult, utilRows] = await Promise.all([
          getWipReportsByProjectIds(client, projectId, monthsParam),
          selectedProject.salesforce_id
            ? getKeyDatesByProjectSFId(client, selectedProject.salesforce_id)
            : Promise.resolve(null),
          utilPromise,
        ])
        if (cancelled) return

        const list = Array.isArray(wipReports) ? wipReports : []
        const row =
          list.find(r => String(r.projectId) === projectId) ||
          list.find(r => r.projectId === selectedProject._id) ||
          list[0] ||
          null

        setWipRow(row ? removeTypename(row) : null)
        const rawKd = kdResult?.keyDates
        const kd = Array.isArray(rawKd) ? removeTypename(rawKd) : []
        setKeyDates(kd || [])
        const u = Array.isArray(utilRows) ? removeTypename(utilRows) : []
        setStaffUtilization(u)
      } catch (e) {
        if (!cancelled) {
          console.error(e)
          setError(e)
          setWipRow(null)
          setKeyDates([])
          setStaffUtilization([])
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [client, selectedProject, monthsParam, selectedMonth])

  const monthlyWipReports = wipRow?.monthlyWipReports || []

  const availableMonths = useMemo(() => {
    const keys = (monthlyWipReports || []).map(m => m?.month).filter(Boolean)
    return [...new Set(keys)].sort()
  }, [monthlyWipReports])

  const rpt = useMemo(() => {
    if (!selectedMonth) return null
    return monthlyWipReports.find(m => m?.month === selectedMonth) || null
  }, [monthlyWipReports, selectedMonth])

  const weeklyPacing = useMemo(() => buildWeeklyPacing(rpt, selectedMonth), [rpt, selectedMonth])
  const pacingUsd = useMemo(() => monthPacingUsd(rpt), [rpt])
  const trend = useMemo(() => deriveTrend(rpt), [rpt])
  const forecast = useMemo(() => computeForecastEom(rpt, selectedMonth, today), [
    rpt,
    selectedMonth,
    today,
  ])
  const historySparks = useMemo(() => buildHistorySparks(monthlyWipReports, selectedMonth), [
    monthlyWipReports,
    selectedMonth,
  ])
  const periodLabel = useMemo(() => reportingPeriodLabel(selectedMonth, today), [
    selectedMonth,
    today,
  ])

  return {
    today,
    monthsParam,
    monthlyWipReports,
    availableMonths,
    rpt,
    keyDates,
    weeklyPacing,
    pacingUsd,
    trend,
    forecast,
    historySparks,
    periodLabel,
    staffUtilization,
    isLoading,
    error,
  }
}
